import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Handshake, Loader2, Send, UserPlus, CheckCircle2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useSeo } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RETI = { youtire: { nome: "YouTire", descrizione: "rete di gommisti partner XMOVE" } };
const input = "w-full rounded-xl border border-xmove-ink/10 bg-white px-3.5 py-2.5 text-sm text-xmove-ink outline-none focus:border-xmove-orange";
const Campo = ({ label, children }) => <label className="block"><span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-xmove-muted">{label}</span>{children}</label>;

const Privacy = ({ id, value, onChange }) => (
  <label className="flex items-start gap-2.5 text-xs leading-relaxed text-xmove-muted">
    <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} data-testid={`${id}-privacy`} className="mt-0.5 h-4 w-4 accent-xmove-orange" />
    <span>Ho letto l'informativa privacy e acconsento al trattamento dei dati; dichiaro di aver raccolto il consenso del cliente segnalato.</span>
  </label>
);

const Registrazione = ({ rete }) => {
  const [f, setF] = useState({ officina: "", referente: "", email: "", telefono: "", citta: "", consenso: false });
  const [stato, setStato] = useState("idle");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const invia = async (e) => {
    e.preventDefault();
    if (!f.consenso) return toast.error("Accetta l'informativa privacy.");
    setStato("loading");
    try {
      const r = await axios.post(`${API}/partner/registrazione`, { ...f, rete });
      setStato("ok");
      toast.success(r.data.gia_registrato ? "Officina già registrata: puoi inviare richieste." : "Officina registrata! Controlla la tua email.");
    } catch (err) {
      setStato("idle");
      toast.error(err.response?.data?.detail || "Registrazione non riuscita.");
    }
  };
  if (stato === "ok") return <div className="flex items-center gap-3 rounded-xl bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="partner-reg-ok"><CheckCircle2 size={18} /> Registrazione completata: ora puoi inviare le richieste dei tuoi clienti con la tua email <b>{f.email}</b>.</div>;
  return (
    <form onSubmit={invia} className="grid gap-4" data-testid="partner-reg-form">
      <Campo label="Nome officina *"><input required value={f.officina} onChange={set("officina")} className={input} data-testid="partner-reg-officina" /></Campo>
      <div className="grid gap-4 sm:grid-cols-2">
        <Campo label="Referente *"><input required value={f.referente} onChange={set("referente")} className={input} data-testid="partner-reg-referente" /></Campo>
        <Campo label="Città"><input value={f.citta} onChange={set("citta")} className={input} data-testid="partner-reg-citta" /></Campo>
        <Campo label="Email officina *"><input required type="email" value={f.email} onChange={set("email")} className={input} data-testid="partner-reg-email" /></Campo>
        <Campo label="Telefono *"><input required type="tel" value={f.telefono} onChange={set("telefono")} className={input} data-testid="partner-reg-telefono" /></Campo>
      </div>
      <Privacy id="partner-reg" value={f.consenso} onChange={(v) => setF({ ...f, consenso: v })} />
      <button disabled={stato === "loading"} data-testid="partner-reg-submit" className="inline-flex items-center justify-center gap-2 rounded-full bg-xmove-night px-6 py-3 text-sm font-bold text-white hover:bg-xmove-orange disabled:opacity-60">
        {stato === "loading" ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Registra la mia officina
      </button>
    </form>
  );
};

const Richiesta = ({ rete }) => {
  const [f, setF] = useState({ email_partner: "", cliente_nome: "", cliente_telefono: "", cliente_email: "", richiesta: "", consenso: false });
  const [stato, setStato] = useState("idle");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const invia = async (e) => {
    e.preventDefault();
    if (!f.consenso) return toast.error("Accetta l'informativa privacy.");
    setStato("loading");
    try {
      await axios.post(`${API}/partner/richieste`, { ...f, rete, cliente_email: f.cliente_email || null });
      setStato("ok");
      toast.success("Richiesta inviata! Ti abbiamo mandato una email di conferma.");
    } catch (err) {
      setStato("idle");
      toast.error(err.response?.data?.detail || "Invio non riuscito.");
    }
  };
  if (stato === "ok") return (
    <div className="rounded-xl bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="partner-req-ok">
      <p className="flex items-center gap-2 font-bold"><CheckCircle2 size={18} /> Richiesta ricevuta</p>
      <p className="mt-1">Richiameremo <b>{f.cliente_nome}</b> e ti aggiorneremo sull'esito. La conferma è nella tua casella <b>{f.email_partner}</b>.</p>
      <button onClick={() => { setF({ ...f, cliente_nome: "", cliente_telefono: "", cliente_email: "", richiesta: "", consenso: false }); setStato("idle"); }} className="mt-3 text-xs font-bold text-xmove-orange hover:underline" data-testid="partner-req-another">Invia un'altra richiesta</button>
    </div>
  );
  return (
    <form onSubmit={invia} className="grid gap-4" data-testid="partner-req-form">
      <Campo label="La tua email di officina registrata *"><input required type="email" value={f.email_partner} onChange={set("email_partner")} className={input} data-testid="partner-req-email-partner" /></Campo>
      <div className="grid gap-4 sm:grid-cols-2">
        <Campo label="Nome e cognome cliente *"><input required value={f.cliente_nome} onChange={set("cliente_nome")} className={input} data-testid="partner-req-nome" /></Campo>
        <Campo label="Telefono cliente *"><input required type="tel" value={f.cliente_telefono} onChange={set("cliente_telefono")} className={input} data-testid="partner-req-telefono" /></Campo>
      </div>
      <Campo label="Email cliente (facoltativa)"><input type="email" value={f.cliente_email} onChange={set("cliente_email")} className={input} data-testid="partner-req-email-cliente" /></Campo>
      <Campo label="Richiesta del cliente *"><textarea required rows={4} value={f.richiesta} onChange={set("richiesta")} placeholder="Es. cerca un SUV ibrido, 36 mesi, 15.000 km/anno, budget 350 €/mese; oppure auto SecondLife a stock…" className={input} data-testid="partner-req-testo" /></Campo>
      <Privacy id="partner-req" value={f.consenso} onChange={(v) => setF({ ...f, consenso: v })} />
      <button disabled={stato === "loading"} data-testid="partner-req-submit" className="inline-flex items-center justify-center gap-2 rounded-full bg-xmove-orange px-6 py-3 text-sm font-bold text-white hover:brightness-105 disabled:opacity-60">
        {stato === "loading" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Invia la richiesta a XMOVE
      </button>
    </form>
  );
};

export default function PartnerPage() {
  const { rete } = useParams();
  const info = RETI[rete];
  const [tab, setTab] = useState("richiesta");
  useSeo({ title: info ? `Area partner ${info.nome} | XMOVE Italia` : "Partner | XMOVE Italia", description: "Pagina riservata ai partner XMOVE per segnalare richieste di noleggio dei propri clienti.", path: `/partner/${rete}/`, noindex: true });

  if (!info) return <div><Header /><div className="pt-40 text-center text-sm text-xmove-muted">Rete partner non trovata. <Link to="/" className="text-xmove-orange">Torna alla home</Link></div></div>;
  return (
    <div data-testid="partner-page">
      <Header />
      <section className="bg-xmove-night pb-14 pt-32 text-white lg:pb-20 lg:pt-40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-xmove-orange"><Handshake size={14} /> Programma partner</p>
            <h1 className="mt-3 font-display text-4xl font-bold leading-tight sm:text-5xl" data-testid="partner-h1">XMOVE × {info.nome}</h1>
            <p className="mt-5 max-w-2xl text-base text-white/70 md:text-lg">Segnala in un minuto la richiesta di noleggio di un tuo cliente: lo richiamiamo noi, tu ricevi una email di conferma per ogni segnalazione e vieni aggiornato sull'esito.</p>
          </Reveal>
        </div>
      </section>
      <section className="mx-auto max-w-3xl px-5 py-12 lg:px-8 lg:py-16">
        <div className="flex gap-2 rounded-full bg-xmove-cream p-1" role="tablist">
          {[["richiesta", "Invia una richiesta cliente"], ["registrazione", "Registra la tua officina"]].map(([k, l]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`partner-tab-${k}`} className={`flex-1 rounded-full px-4 py-2.5 text-sm font-bold transition-colors ${tab === k ? "bg-xmove-night text-white" : "text-xmove-muted hover:text-xmove-ink"}`}>{l}</button>
          ))}
        </div>
        <div className="mt-8 rounded-2xl border border-xmove-ink/8 bg-white p-6 shadow-soft sm:p-8">
          {tab === "richiesta" ? (
            <>
              <p className="mb-6 text-sm text-xmove-muted">Non sei ancora registrato? <button onClick={() => setTab("registrazione")} className="font-bold text-xmove-orange hover:underline" data-testid="partner-goto-reg">Registra prima la tua officina</button> (bastano 30 secondi).</p>
              <Richiesta rete={rete} />
            </>
          ) : <Registrazione rete={rete} />}
        </div>
      </section>
      <Footer />
    </div>
  );
}
